import React, { memo, useEffect, useRef } from 'react'

import { CABIN_MEDIA_IMAGE_SLIDE_DURATION_MS } from './constants'
import { isVideoMediaUrl } from './isVideoMediaUrl'
import { CabinMediaItem } from './types'

type CabinMediaSlideProps = {
    item: CabinMediaItem
    isActive: boolean
    onPlaybackProgress: (progress: number) => void
    onMediaComplete: () => void
}

const mediaFillStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
}

const CabinMediaSlide = memo(({ item, isActive, onPlaybackProgress, onMediaComplete }: CabinMediaSlideProps) => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const imageProgressFrameRef = useRef<number>()
    const imageStartTimeRef = useRef<number>(0)

    const isVideo = isVideoMediaUrl(item.src)

    useEffect(() => {
        if (!isActive || !isVideo) return

        const video = videoRef.current
        if (!video) return

        const handleTimeUpdate = () => {
            if (!video.duration) return
            onPlaybackProgress(Math.min(video.currentTime / video.duration, 1))
        }

        const handleEnded = () => {
            onPlaybackProgress(1)
            onMediaComplete()
        }

        video.currentTime = 0
        onPlaybackProgress(0)

        const playPromise = video.play()
        if (playPromise) {
            playPromise.catch(() => {
                video.muted = true
                video.play().catch(() => {})
            })
        }

        video.addEventListener('timeupdate', handleTimeUpdate)
        video.addEventListener('ended', handleEnded)

        return () => {
            video.pause()
            video.removeEventListener('timeupdate', handleTimeUpdate)
            video.removeEventListener('ended', handleEnded)
        }
    }, [isActive, isVideo, item.src, onMediaComplete, onPlaybackProgress])

    useEffect(() => {
        if (!isActive || isVideo) return

        imageStartTimeRef.current = performance.now()
        onPlaybackProgress(0)

        const tick = (timestamp: number) => {
            const elapsed = timestamp - imageStartTimeRef.current
            const progress = Math.min(elapsed / CABIN_MEDIA_IMAGE_SLIDE_DURATION_MS, 1)
            onPlaybackProgress(progress)

            if (progress >= 1) {
                onMediaComplete()
                return
            }

            imageProgressFrameRef.current = requestAnimationFrame(tick)
        }

        imageProgressFrameRef.current = requestAnimationFrame(tick)

        return () => {
            if (imageProgressFrameRef.current) {
                cancelAnimationFrame(imageProgressFrameRef.current)
            }
        }
    }, [isActive, isVideo, item.src, onMediaComplete, onPlaybackProgress])

    if (!isActive) return null

    if (isVideo) {
        return (
            <video
                ref={videoRef}
                key={item.id}
                style={mediaFillStyle}
                src={item.src}
                playsInline
                preload="auto"
            />
        )
    }

    return (
        <img
            key={item.id}
            src={item.src}
            alt={item.title || ''}
            style={mediaFillStyle}
        />
    )
})

CabinMediaSlide.displayName = 'CabinMediaSlide'

export default CabinMediaSlide
